import { AdvancedTechnologyEdaVendor } from '@prisma/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  AdvancedTechnologyEdaVendorDetail,
  CreateAdvancedTechnologyEdaVendorDto,
  FindAdvancedTechnologyEdaVendorParams,
} from '@app/domain-maps-interfaces';
import { CommonFindResult } from '@app/infra-interfaces';
import { Axios } from '@app/web-components';

import { bundleKeys } from '../../../../../bundles/data';

const EDA_VENDOR_ALL_QUERY_KEY = 'EDA_VENDOR_ALL_QUERY_KEY';

const edaVendorKeys = {
  all: [EDA_VENDOR_ALL_QUERY_KEY] as const,
  lists: () => [...edaVendorKeys.all, 'list'] as const,
  list: (filters: object) => [...edaVendorKeys.lists(), filters] as const,
  details: () => [...edaVendorKeys.all, 'detail'] as const,
  detail: (id: number) => [...edaVendorKeys.details(), id] as const,
};

export const useEdaVendor = (id: number) => {
  const axios = Axios.useAxiosApi();

  return useQuery<AdvancedTechnologyEdaVendorDetail>({
    queryKey: edaVendorKeys.detail(id),
    queryFn: async () => {
      const { data } = await axios.get(`advanced-technology-eda-vendor/${id}`);

      return data;
    },
  });
};

export const useEdaVendors = (query: FindAdvancedTechnologyEdaVendorParams) => {
  const axios = Axios.useAxiosApi();

  return useQuery<CommonFindResult<AdvancedTechnologyEdaVendorDetail>>({
    queryKey: edaVendorKeys.list(query),
    queryFn: async () => {
      const { data } = await axios.post('advanced-technology-eda-vendor/find', query);

      return data;
    },
  });
};

export const useEdaVendorExists = () => {
  const axios = Axios.useAxiosApi();

  return useMutation({
    mutationFn: async (externalId: number) => {
      const args: FindAdvancedTechnologyEdaVendorParams = {
        where: { supplier: { externalId } },
      };

      const response = await axios.post<CommonFindResult<AdvancedTechnologyEdaVendorDetail>>(
        '/advanced-technology-eda-vendor/find',
        args,
      );

      return response.data.count === 1 ? response.data.data[0] : undefined;
    },
  });
};

export const useCreateEdaVendor = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateAdvancedTechnologyEdaVendorDto) => {
      return axios.post<AdvancedTechnologyEdaVendor>('/advanced-technology-eda-vendor', dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: edaVendorKeys.all });
      queryClient.invalidateQueries({ queryKey: bundleKeys.all });
    },
  });
};

export const useEditEdaVendor = (id: number) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateAdvancedTechnologyEdaVendorDto) => {
      return axios.patch<AdvancedTechnologyEdaVendor>('/advanced-technology-eda-vendor', { id, ...dto });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: edaVendorKeys.all });
      queryClient.invalidateQueries({ queryKey: bundleKeys.all });
    },
  });
};

export const useDeleteEdaVendor = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => {
      return axios.delete<AdvancedTechnologyEdaVendor>(`/advanced-technology-eda-vendor/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: edaVendorKeys.all });
      queryClient.invalidateQueries({ queryKey: bundleKeys.all });
    },
  });
};
